import * as React from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  Tooltip,
  Flex,
  Skeleton,
  Stack,
} from '@chakra-ui/react';

import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  ColumnDef,
  SortingState,
  getSortedRowModel,
  getExpandedRowModel,
} from '@tanstack/react-table';
import NoData from './NoData';
import Loader from '../appLoader';
import TableContainer from './NestedGridTable.styled';
import './styles.css';
import { SortIcon, SubtractSquareIcon } from '../icons';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';

type Sticky = 'firstCol' | 'secondCol98' | 'none';

type DataGridProps<Data extends object> = {
  data: Data[];
  columns: ColumnDef<any, any>[];
  maxH?: string;
  useMaxH?: boolean;
  showLoader: boolean;
  skeletonLoader?: boolean;
  sticky: Sticky;
  noDataView?: React.ReactNode;
  hideHeaderTooltip?: boolean;
  disableRowHover?: boolean;
  disableBuiltinLines?: boolean;
  renderSubComponent: (props: {
    row: any;
    firstColumnWidth: number | undefined;
  }) => React.ReactElement | null;
  getRowCanExpand: (row: any) => boolean;
  getSubRows: (rows: any) => Data[];
};

/**
 * A nested grid table component supporting expandable rows.
 *
 * @param param0 - Props for configuring the table.
 *   - `renderSubComponent`: Function that renders a custom component below a row when expanded. Add ()=> <></> if there is no * SubComponent
 *   - `getSubRows`: Function to retrieve nested subRows from a given row. If subComponent is there, add ()=>[]
 *   - `getRowCanExpand`: Function to determine if a row is expandable based on its data.
 *
 * @returns A React component rendering a nested, expandable data grid table.
 */

export function NestedGridTable<Data extends object>({
  data,
  columns,
  showLoader = false,
  skeletonLoader = false,
  maxH,
  useMaxH,
  sticky,
  noDataView,
  hideHeaderTooltip = false,
  disableRowHover = false,
  disableBuiltinLines = false,
  renderSubComponent,
  getRowCanExpand,
  getSubRows,
}: DataGridProps<Data>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [rowSelection, setRowSelection] = React.useState<any>([]);
  const [expanded, setExpanded] = React.useState({});
  const firstColumnRef = React.useRef<HTMLTableCellElement | null>(null);
  const [firstColumnWidth, setFirstColumnWidth] = React.useState<
    number | undefined
  >(undefined);

  const table = useReactTable({
    columns,
    data,
    getRowCanExpand,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: getSubRows,
    state: {
      sorting,
      rowSelection,
      expanded,
    },
  });

  React.useEffect(() => {
    if (!firstColumnRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setFirstColumnWidth(entry.contentRect.width);
      }
    });

    observer.observe(firstColumnRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <TableContainer maxH={maxH} useMaxH={useMaxH}>
      {skeletonLoader ? (
        <Stack spacing={3}>
          {new Array(12).fill(null)?.map((_, index: number) => (
            <Skeleton key={index} height="48px" width="full" />
          ))}
        </Stack>
      ) : (
        <Table
          className={
            sticky === 'firstCol'
              ? 'sticky-table-column1'
              : sticky === 'secondCol98'
              ? 'sticky-table-column1 secondCol'
              : ''
          }
        >
          <Thead position={'sticky'} bg="#FBFBFB" top={0} zIndex={2}>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const meta: any = header.column.columnDef.meta;
                  return (
                    <Tooltip
                      key={header.id}
                      aria-label="table header"
                      label={flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      placement="bottom"
                      isDisabled={hideHeaderTooltip}
                    >
                      <Th
                        ref={index === 0 ? firstColumnRef : undefined}
                        textTransform="none"
                        onClick={header.column.getToggleSortingHandler()}
                        isNumeric={meta?.isNumeric}
                        fontSize="sm"
                        // bg="#FBFBFB"
                        boxShadow="inset 0 -1px 0 0 rgba(0, 0, 0, 0.1)"
                        fontFamily="heading"
                        fontWeight="medium"
                        letterSpacing={0}
                        color="text.secondary2"
                      >
                        <Flex
                          align="center"
                          userSelect={'none'}
                          {...{
                            style: {
                              width: header.getSize(),
                            },
                          }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          <Box as="div" pl={2} userSelect={'initial'}>
                            {header.column.getCanSort() ? (
                              header.column.getIsSorted() ? (
                                header.column.getIsSorted() === 'desc' ? (
                                  <MdKeyboardArrowDown
                                    aria-label="sorted descending"
                                    color="#2A2A2F"
                                  />
                                ) : (
                                  <MdKeyboardArrowUp
                                    aria-label="sorted ascending"
                                    color="#2A2A2F"
                                  />
                                )
                              ) : (
                                <Flex direction="column">
                                  <SortIcon />
                                </Flex>
                              )
                            ) : (
                              <></>
                            )}
                          </Box>
                        </Flex>
                      </Th>
                    </Tooltip>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <React.Fragment key={row.id}>
                <Tr
                  // bg={
                  //   row.depth === 0
                  //     ? ''
                  //     : row.depth === 1
                  //     ? '#F6F7F9'
                  //     : undefined
                  // }
                  _hover={{ backgroundColor: disableRowHover ? '' : '#E3E6EA' }}
                  borderBottom="none"
                >
                  {row.getVisibleCells().map((cell, index) => {
                    const meta: any = cell.column.columnDef.meta;
                    const isSubRow = row.depth > 0;

                    const parentRow = table
                      .getRowModel()
                      .rows.find((r) =>
                        r.subRows?.some((subRow) => subRow.id === row.id)
                      );

                    const isLastChild =
                      parentRow?.subRows[parentRow.subRows.length - 1]?.id ===
                      row.id;

                    return (
                      <Td
                        position="relative"
                        bg={index === 0 ? '#FBFBFB' : ''}
                        key={cell.id}
                        fontFamily="heading"
                        color="text.primary"
                        fontSize="sm"
                        verticalAlign="top"
                        fontWeight="medium"
                        isNumeric={meta?.isNumeric}
                        border="none"
                        boxShadow={
                          index === 0
                            ? 'inset -1px 0 0 0 rgba(0, 0, 0, 0.1)'
                            : 'inset 0 -1px 0 0 rgba(0, 0, 0, 0.1), inset -1px 0 0 0 rgba(0, 0, 0, 0.1)'
                        }
                        style={{ width: cell.column.getSize() }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}

                        {index === 0 && !disableBuiltinLines && (
                          <Box
                            position="absolute"
                            top={0}
                            left={`${18 + row.depth * 14}px`}
                            height="100%"
                          >
                            {isSubRow && !isLastChild && (
                              <Box position="relative">
                                <Box
                                  position="absolute"
                                  top="50%"
                                  left="0px"
                                  height="calc(100% - 50%)"
                                  width="1px"
                                  bg="text.secondary2"
                                />
                              </Box>
                            )}

                            {isSubRow && isLastChild && (
                              <Box
                                bg="white"
                                position="absolute"
                                bottom="40px"
                                left="-6px"
                                zIndex={99}
                              >
                                <SubtractSquareIcon />
                              </Box>
                            )}
                          </Box>
                        )}

                        {index === 0 &&
                          !disableBuiltinLines &&
                          Array.from({ length: row.depth }).map(
                            (_, depthIndex) => (
                              <Box
                                key={depthIndex}
                                position="absolute"
                                top={0}
                                left={`${32 + depthIndex * 14}px`}
                                height="100%"
                              >
                                {isSubRow && (
                                  <Box
                                    position="absolute"
                                    top={0}
                                    left="0.4px"
                                    height={
                                      isLastChild &&
                                      depthIndex === row.depth - 1
                                        ? '50%'
                                        : '100%'
                                    }
                                    width="0.5px"
                                    bg="#626266"
                                  />
                                )}
                              </Box>
                            )
                          )}
                      </Td>
                    );
                  })}
                </Tr>

                {row.getIsExpanded() && (
                  <>
                    {renderSubComponent ? (
                      <Tr>
                        <Td
                          boxShadow="inset 0 -1px 0 0 rgba(0, 0, 0, 0.1), inset -1px 0 0 0 rgba(0, 0, 0, 0.1)"
                          p={0}
                          colSpan={row.getVisibleCells().length}
                          bg="white"
                        >
                          {renderSubComponent({ row, firstColumnWidth })}
                        </Td>
                      </Tr>
                    ) : (
                      <></>
                    )}
                  </>
                )}
              </React.Fragment>
            ))}
          </Tbody>
        </Table>
      )}

      {!showLoader &&
        !skeletonLoader &&
        table.getRowModel().rows.length < 1 && (
          <NoData noDataView={noDataView} />
        )}
      {showLoader && !skeletonLoader && <Loader />}
    </TableContainer>
  );
}
